import { ReactNode } from "react";
import {
  Document,
  Page,
  Image,
  Text,
  View,
  StyleSheet,
  Link,
} from "@react-pdf/renderer";
import { FetchedReportData } from "../types";
import { formatDate } from "./dateUtils";

import stampSeal from "../images/stamp-seal.png";
import darkLogo from "../images/logo-black.png";
import {
  formattedVehicleInfo,
  socialsJointText,
} from "../components/SubmitTip/resources";

// API base URL for fetching media
const API_BASE_URL = process.env.REACT_APP_API_URL || "";

// Styles
const styles = StyleSheet.create({
  container: {
    backgroundColor: "#fff",
    padding: 18,
    border: "1px solid #000",
    borderRadius: 20,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
  },
  subtitle: {
    fontSize: 10,
    color: "#000",
  },
  section: {
    marginBottom: 16,
    padding: 12,
    backgroundColor: "#F6F6F6",
    borderRadius: 8,
  },
  sectionHeader: {
    paddingBottom: 8,
    marginBottom: 8,
    borderBottom: "1px solid #EEE",
    fontWeight: "bold",
  },
  detailRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  label: {
    flex: 1, // Allow label to take up required space
    fontSize: 12,
    fontWeight: 900,
    marginRight: 4, // Add some space between label and value
  },
  value: {
    flex: 2, // Allow value to take up more space
    textAlign: "right",
    fontSize: 10,
    fontWeight: 400,
    color: "#1E1E1E",
    maxWidth: "70%", // Restrict width of the value to prevent overflow
    flexWrap: "wrap", // Ensure text wraps to the next line if too long
  },
  footer: {
    marginTop: 24,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  signatureContainer: {
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 2,
  },
  signatureText: {
    fontSize: 8,
  },
  mediaSection: {
    flexDirection: "column",
    alignItems: "flex-start",
    marginTop: 8,
    gap: 4,
  },
  imagesContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
    marginTop: 8,
  },
  imageItem: {
    width: "30%", // Approx 3 images per row
    height: 80,
    objectFit: "cover",
    borderRadius: 4,
  },
  audioLink: {
    color: "blue",
    textDecoration: "underline",
    fontSize: 10,
  },
  mediaLabel: {
    fontSize: 12,
    fontWeight: 900,
    marginBottom: 4,

    flex: 2, // Allow value to take up more space
    textAlign: "right",
    color: "#1E1E1E",
    maxWidth: "70%", // Restrict width of the value to prevent overflow
    flexWrap: "wrap", // Ensure text wraps to the next line if too long
  },
});

// Reusable Section Component
const ReportSection = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <View style={styles.section}>
    <Text style={styles.sectionHeader}>{title}</Text>
    {children}
  </View>
);

// Reusable Detail Row
const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value: string | undefined;
}) => (
  <View style={styles.detailRow}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{value}</Text>
  </View>
);

// Process media URLs
const getMediaUrl = (path?: string) => {
  if (!path) return "";
  // If already a full URL, return as is
  if (path.startsWith("http")) return path;
  // Otherwise, combine with API base URL
  return `${API_BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
};

// Images Gallery for PDF
const ImagesGallery = ({ images }: { images: string[] }) => (
  <View style={styles.imagesContainer}>
    {images.slice(0, 6).map((image, index) => (
      <Image
        key={index}
        src={{
          uri: image,
          method: "GET",
          headers: { "Cache-Control": "no-cache" },
          body: undefined,
        }}
        // src={image}
        style={styles.imageItem}
      />
    ))}
    {images.length > 6 && (
      <Text style={{ fontSize: 8, marginTop: 5 }}>
        +{images.length - 6} more images
      </Text>
    )}
  </View>
);

// PDF Template Component
const UserReportPDFTemplate = ({
  report,
}: {
  report: FetchedReportData | undefined;
}) => {
  if (!report) {
    return null;
  }

  // Handle voice narration audio
  const voiceNarrationUrl = report.incidentDescription?.incidentVoiceNarration
    ? getMediaUrl(report.incidentDescription.incidentVoiceNarration[0])
    : "";

  // Handle incident evidence images
  const evidenceImageUrls = report.incidentDescription?.incidentPhotoEvidence
    ? report.incidentDescription.incidentPhotoEvidence.map(getMediaUrl)
    : [];

  // Handle abuser photo evidence
  const abuserPhotoUrls =
    report.hasAbuserInfo && report.abuserDetails?.abuserPhotoDescription
      ? report.abuserDetails.abuserPhotoDescription.map(getMediaUrl)
      : [];

  return (
    <Document>
      <Page size="A4" style={{ padding: 20 }}>
        <View style={styles.container}>
          {/* Header */}
          <View style={styles.header}>
            <Image src={darkLogo} style={{ height: 10 }} />
            <Text style={styles.subtitle}>
              Reported Date: {formatDate(report.createdAt, "mmddyyyy")}
            </Text>
          </View>

          {/* Case Report */}
          <View style={{ gap: 10, marginBottom: 20 }}>
            <Text style={{ fontSize: 18, fontWeight: 900 }}>Case Report</Text>
            <Text style={{ fontSize: 12, fontWeight: 900 }}>
              N<Text style={{ fontSize: 8, marginTop: -2 }}>o</Text>:{" "}
              {report._id}
            </Text>
          </View>

          {/* Report Details */}
          <View>
            {/* Reporter Details */}
            <ReportSection title="Reporter's Information">
              <DetailRow label="Role" value={report.reporterDetails.role} />
              <DetailRow label="Gender" value={report.reporterDetails.gender} />
              <DetailRow label="Age" value={report.reporterDetails.age} />
              <DetailRow
                label="Ethnicity"
                value={report.reporterDetails?.ethnicity || "Not Available"}
              />
              <DetailRow
                label="Phone Number"
                value={report.reporterDetails.phone}
              />
              <DetailRow label="Email" value={report.reporterDetails.email} />
              <DetailRow
                label="Socials"
                value={socialsJointText(report.reporterDetails.socials)}
              />
            </ReportSection>

            {/* Incident Details */}
            <ReportSection title="Details of the Incident">
              <DetailRow label="Report" value={report.caseName} />
              <DetailRow label="Type" value={report.caseType} />
              <DetailRow
                label="Date of the Incident"
                value={formatDate(report.incidentDescription.date, "mmddyyyy")}
              />
              <DetailRow
                label="Location"
                value={report.incidentDescription.location}
              />
              {["social media", "online"].includes(
                report.incidentDescription.location
              ) && (
                <DetailRow
                  label="Online Locations/Links"
                  value={
                    report.incidentDescription.digitalcaseLinks?.join(", ") ||
                    "N/A"
                  }
                />
              )}
              <DetailRow
                label="State"
                value={report.incidentDescription.detailedLocation.state}
              />
              <DetailRow
                label="County"
                value={report.incidentDescription.detailedLocation.county}
              />
              <DetailRow
                label="City"
                value={report.incidentDescription.detailedLocation.city}
              />
              <DetailRow
                label="Description"
                value={report.incidentDescription.description}
              />

              {/* Voice Narration Link */}
              {voiceNarrationUrl && (
                <View style={styles.detailRow}>
                  <Text style={styles.label}>Voice Narration:</Text>
                  <Link src={voiceNarrationUrl} style={styles.audioLink}>
                    Listen to voice narration
                  </Link>
                </View>
              )}

              {/* Incident Evidence Photos */}
              {evidenceImageUrls.length > 0 && (
                <View style={styles.mediaSection}>
                  <Text style={styles.mediaLabel}>Photo Evidence:</Text>
                  <ImagesGallery images={evidenceImageUrls} />
                </View>
              )}
            </ReportSection>

            {/* Perpetrator Details */}
            <ReportSection title="Perpetrator's Details">
              <DetailRow label="Name" value={report.abuserDetails?.name} />
              <DetailRow label="Alias" value={report.abuserDetails?.nickname} />
              <DetailRow label="Gender" value={report.abuserDetails?.gender} />
              <DetailRow
                label="Reference Age"
                value={report.abuserDetails?.refAge}
              />
              <DetailRow
                label="Relationship to Victim"
                value={report.abuserDetails?.relationToVictim}
              />
              <DetailRow
                label="Ethnicity"
                value={report.abuserDetails?.ethnicity || "Not Available"}
              />
              <DetailRow
                label="Socials"
                value={socialsJointText(report.abuserDetails?.socials)}
              />
              <DetailRow
                label="Occupation"
                value={report.abuserDetails?.occupation}
              />
              <DetailRow
                label="Special Authority Position"
                value={
                  report.abuserDetails?.hasSpecialAuthority
                    ? report.abuserDetails.specialAuthorityPosition
                    : "None"
                }
              />
              <DetailRow
                label="Physical Description"
                value={report.abuserDetails?.phyDescription}
              />
              <DetailRow
                label="Vehicle Information"
                value={
                  report.abuserDetails?.hasAbuserVehicleInfo
                    ? formattedVehicleInfo(report.abuserDetails.vehicleInfo)
                    : "Not Available"
                }
              />

              {/* Support section */}
              <DetailRow
                label="Need an attorney?"
                value={report.NeedAnAttorney ? "Yes" : "No"}
              />
              <DetailRow
                label="Need counseling?"
                value={report.NeedCounseling ? "Yes" : "No"}
              />
              <DetailRow
                label="Need a private investigator?"
                value={report.NeedPrivateInvestigator ? "Yes" : "No"}
              />

              {/* Abuser Photos */}
              {abuserPhotoUrls.length > 0 && (
                <View style={styles.mediaSection}>
                  <Text style={styles.mediaLabel}>Abuser Photos:</Text>
                  <ImagesGallery images={abuserPhotoUrls} />
                </View>
              )}
            </ReportSection>
          </View>

          {/* Footer */}
          <View style={styles.footer}>
            <Image src={stampSeal} style={{ height: 121 }} />
            <View style={styles.signatureContainer}>
              <Image src={darkLogo} style={{ height: 10, marginBottom: 2 }} />
              <Text style={styles.signatureText}>
                Privacy policies and terms of conditions available at
                www.unspokenn.com
              </Text>
              <Text style={styles.signatureText}>
                <a
                  href="mailto:info@unspokenn.com"
                  style={{ textDecoration: "underline" }}
                >
                  info@unspokenn.com
                </a>
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default UserReportPDFTemplate;
